import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { getArticle } from "@/content/articles";
import { useSeo } from "@/lib/useSeo";
import { FadeUp } from "@/components/Reveal";
import { Button } from "@/components/ui/button";

const formatDate = (iso, lang) =>
  new Date(iso + "T12:00:00").toLocaleDateString(lang === "el" ? "el-GR" : "en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const ArticleDetail = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const article = getArticle(slug);

  const item = article && article[lang];
  const override =
    article && item
      ? {
          el: {
            path: `/articles/${article.slug}`,
            title: `${item.title} | Ιωάννης Τσίρος`,
            description: item.excerpt,
          },
          en: {
            path: `/articles/${article.slug}`,
            title: `${item.title} | Ioannis Tsiros`,
            description: item.excerpt,
          },
        }
      : null;
  useSeo("article", override);

  if (!article || !item) {
    return <Navigate to="/articles" replace />;
  }

  const back = lang === "el" ? "Όλα τα άρθρα" : "All articles";
  const ctaTitle = lang === "el" ? "Θέλετε να το συζητήσουμε;" : "Shall we talk about it?";
  const ctaText =
    lang === "el"
      ? "Η πρώτη γνωριμία 15 λεπτών είναι δωρεάν, online και χωρίς δέσμευση."
      : "The free 15-minute introduction is online, free of charge, with no commitment.";

  return (
    <main data-testid="page-article">
      <article className="container-x pb-24 pt-32 md:pt-40 lg:pt-44">
        <FadeUp className="mx-auto max-w-2xl">
          <Link
            to="/articles"
            data-testid="article-back-link"
            className="flex w-fit items-center gap-2 text-sm font-medium text-sage-700 transition-colors duration-200 hover:text-clay"
          >
            <ArrowLeft className="h-4 w-4" /> {back}
          </Link>
          <p className="mt-8 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            {formatDate(article.date, lang)} · {item.minutes}{" "}
            {lang === "el" ? "λεπτά ανάγνωση" : "min read"}
          </p>
          <h1 className="mt-3 font-display text-3xl leading-[1.1] text-sage-900 sm:text-4xl lg:text-5xl">
            {item.title}
          </h1>
        </FadeUp>

        <FadeUp delay={0.1} className="mx-auto mt-10 max-w-2xl">
          <div className="space-y-6 text-lg leading-relaxed text-foreground/85">
            {item.body.map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </div>
        </FadeUp>

        <FadeUp className="mx-auto mt-16 max-w-2xl">
          <div className="grain-card bg-card p-8" data-testid="article-cta">
            <h2 className="font-display text-2xl text-sage-900">{ctaTitle}</h2>
            <p className="mt-3 text-base leading-relaxed text-foreground/75">{ctaText}</p>
            <Button
              asChild
              data-testid="article-cta-book"
              className="mt-6 rounded-full bg-sage-600 px-7 text-background hover:bg-sage-700"
            >
              <Link to="/intake">
                {lang === "el" ? "Κλείστε Ραντεβού" : "Book a Consultation"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </FadeUp>
      </article>
    </main>
  );
};

export default ArticleDetail;
